/**
 * Customer Position Overview Component
 * Displays customer quote positions similar to supplier QuoteRequestSummary
 * Enhanced with supplier quote integration when available
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Assessment as QuoteIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon
} from '@mui/icons-material';

import { useNotification } from '../../../contexts/NotificationContext';

// Types for Customer Position Data
export interface CustomerPosition {
  id: string;
  customer_case_id: string;
  position_number: number;
  quantity: number;
  part_name?: string;
  part_number?: string;
  notes?: string;
  type_specific_data: {
    // Newer format
    selected_types?: string[];
    type_configurations?: {
      [key: string]: {
        material_spec?: string;
        [key: string]: any;
      };
    };
    // Older format
    material?: string;
    specifications?: string;
    [key: string]: any; // Allow additional properties
  };
  technical_drawing_file?: string;
  created_at: string;
  updated_at: string;
}

export interface SupplierQuoteMapping {
  id: string;
  customer_quote_position_id: string;
  supplier_case_id: string;
  supplier_position_number: number;
  quoted_price?: number;
  delivery_time_days?: number;
  supplier_notes?: string;
  created_at: string;
}

export interface RelatedSupplierCase {
  id: string;
  case_number: string;
  supplier_name: string;
  workflow_state: string;
  created_at: string;
  metadata?: any;
}

interface CustomerPositionOverviewProps {
  caseId: string;
  caseNumber?: string;
  customerCaseData?: any;
  onEditPositions?: () => void;
  onManageSupplierQuotes?: () => void;
}

export const CustomerPositionOverview: React.FC<CustomerPositionOverviewProps> = ({
  caseId,
  caseNumber,
  customerCaseData,
  onEditPositions,
  onManageSupplierQuotes
}) => {
  const [loading, setLoading] = useState(true);
  const [customerPositions, setCustomerPositions] = useState<CustomerPosition[]>([]);
  const [supplierQuoteMappings, setSupplierQuoteMappings] = useState<SupplierQuoteMapping[]>([]);
  const [relatedSupplierCases, setRelatedSupplierCases] = useState<RelatedSupplierCase[]>([]);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadCustomerPositionData();
  }, [caseId]);

  const loadCustomerPositionData = async () => {
    setLoading(true);
    try {
      console.log('🔍 [CustomerPositionOverview] Loading positions for case:', caseId);
      
      // Load customer positions
      const positionsResponse = await fetch(`/api/v3/cases/${caseId}/positions`);
      const positionsData = await positionsResponse.json();
      
      console.log('📊 [CustomerPositionOverview] Positions API response:', positionsData);
      
      if (positionsData.success) {
        const positions = positionsData.data || positionsData.positions || [];
        console.log('✅ [CustomerPositionOverview] Found positions:', positions.length);
        setCustomerPositions(positions);
      } else {
        console.error('❌ [CustomerPositionOverview] Positions API failed:', positionsData.message);
        setCustomerPositions([]);
      }

      // Load supplier quote mappings
      const mappingsResponse = await fetch(`/api/v3/cases/${caseId}/supplier-quote-mappings`);
      const mappingsData = await mappingsResponse.json();
      
      console.log('📊 [CustomerPositionOverview] Mappings API response:', mappingsData);
      
      if (mappingsData.success) {
        const mappings = mappingsData.mappings || mappingsData.data || [];
        console.log('✅ [CustomerPositionOverview] Found mappings:', mappings.length);
        setSupplierQuoteMappings(mappings);
      } else {
        console.log('ℹ️ [CustomerPositionOverview] No mappings found or API failed:', mappingsData.message);
        setSupplierQuoteMappings([]);
      }

      // Load related supplier cases
      const supplierCasesResponse = await fetch(`/api/v3/cases/${caseId}/supplier-cases`);
      const supplierCasesData = await supplierCasesResponse.json();
      
      console.log('📊 [CustomerPositionOverview] Supplier cases API response:', supplierCasesData);
      
      if (supplierCasesData.success) {
        const cases = supplierCasesData.supplier_cases || supplierCasesData.data || [];
        console.log('✅ [CustomerPositionOverview] Found supplier cases:', cases.length);
        setRelatedSupplierCases(cases);
      } else {
        console.log('ℹ️ [CustomerPositionOverview] No supplier cases found or API failed:', supplierCasesData.message);
        setRelatedSupplierCases([]);
      }

    } catch (error) {
      console.error('Error loading customer position data:', error);
      showNotification('Error loading customer position data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPositionTypeDisplay = (position: CustomerPosition) => {
    // Handle newer format with selected_types
    const types = position.type_specific_data?.selected_types || [];
    if (types.length > 0) {
      return types.map(type => type.charAt(0).toUpperCase() + type.slice(1)).join(', ');
    }
    
    // Handle older format with direct material field
    if (position.type_specific_data?.material) {
      return 'Material';
    }
    
    return 'General';
  };

  const getMaterialSpec = (position: CustomerPosition) => {
    // Handle newer format with type_configurations
    const configs = position.type_specific_data?.type_configurations || {};
    if (Object.keys(configs).length > 0) {
      const specs = Object.values(configs).map((config: any) => config.material_spec).filter(Boolean);
      return specs.join(', ') || 'Not specified';
    }
    
    // Handle older format with direct material/specifications fields
    const material = position.type_specific_data?.material;
    const specifications = position.type_specific_data?.specifications;
    
    if (material && specifications) {
      return `${material} - ${specifications}`;
    } else if (material) {
      return material;
    } else if (specifications) {
      return specifications;
    }
    
    return 'Not specified';
  };

  const getSupplierQuotesForPosition = (positionId: string) => {
    return supplierQuoteMappings.filter(mapping => 
      mapping.customer_quote_position_id === positionId
    );
  };

  const getSupplierCaseProgress = () => {
    if (relatedSupplierCases.length === 0) return null;
    
    const stateWeights = {
      'supplier_case_created': 1,
      'supplier_quote_generated': 2,
      'supplier_contacted': 3,
      'supplier_response_received': 4,
      'quote_selected': 5,
      'converted_to_order': 6,
      'case_closed': 7
    };

    const averageProgress = relatedSupplierCases.reduce((sum, supplierCase) => {
      return sum + (stateWeights[supplierCase.workflow_state as keyof typeof stateWeights] || 1);
    }, 0) / relatedSupplierCases.length;

    const percentage = Math.round((averageProgress / 7) * 100);
    return { percentage, total: relatedSupplierCases.length };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Loading customer positions...
        </Typography>
      </Box>
    );
  }

  const supplierProgress = getSupplierCaseProgress();

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Customer Position Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Case {caseNumber || caseId} • Customer: {customerCaseData?.customer_name || 'Unknown'}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={loadCustomerPositionData}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onEditPositions && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={onEditPositions}
              >
                Edit Positions
              </Button>
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Quick Stats */}
          <Grid item xs={12} sm={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">Positions & Files</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" fontWeight="medium">
                  {customerPositions.length} position{customerPositions.length !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2" display="flex" alignItems="center">
                  {customerPositions.filter(p => p.technical_drawing_file).length}
                  <AttachFileIcon sx={{ ml: 0.5, fontSize: 14 }} />
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Supplier Quote Status (Selected Responses) */}
          <Grid item xs={12} sm={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">Supplier Quotes</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <QuoteIcon color="primary" sx={{ fontSize: 16 }} />
                {(() => {
                  const selectedQuotes = relatedSupplierCases.filter(
                    supplierCase => supplierCase.workflow_state === 'quote_selected'
                  );
                  return selectedQuotes.length > 0 ? (
                    <Typography variant="body2">
                      {selectedQuotes.length} quote{selectedQuotes.length !== 1 ? 's' : ''} selected
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No supplier responses selected!
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          </Grid>

          {/* Supplier Cases Progress (Generated Quote Requests) */}
          <Grid item xs={12} sm={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">Supplier Cases</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <BusinessIcon color="primary" sx={{ fontSize: 16 }} />
                {supplierQuoteMappings.length > 0 ? (
                  <Typography variant="body2">
                    {supplierQuoteMappings.length} supplier quote{supplierQuoteMappings.length !== 1 ? 's' : ''} generated
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No supplier cases
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Customer Positions Table */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Position Details
            </Typography>
            {customerPositions.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 1 }}>Pos.</TableCell>
                      <TableCell sx={{ py: 1 }}>Part Details</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>Qty</TableCell>
                      <TableCell sx={{ py: 1 }}>Type/Material</TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>Supplier Quotes</TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>Files</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerPositions.map((position) => {
                      const positionQuotes = getSupplierQuotesForPosition(position.id);
                      return (
                        <TableRow key={position.id}>
                          <TableCell sx={{ py: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              #{position.position_number}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box>
                              {position.part_name && (
                                <Typography variant="caption" fontWeight="medium">
                                  {position.part_name}
                                </Typography>
                              )}
                              {position.part_number && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {position.part_number}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <Typography variant="caption" fontWeight="medium">
                              {position.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box>
                              <Typography variant="caption" display="block">
                                {getPositionTypeDisplay(position)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {getMaterialSpec(position)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            {positionQuotes.length > 0 ? (
                              <Badge badgeContent={positionQuotes.length} color="primary">
                                <CheckCircleIcon color="success" fontSize="small" />
                              </Badge>
                            ) : (
                              <ScheduleIcon color="disabled" fontSize="small" />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            {position.technical_drawing_file ? (
                              <AttachFileIcon color="primary" fontSize="small" />
                            ) : (
                              <AttachFileIcon color="disabled" fontSize="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                No customer positions found. Use the Customer Case Creation Form to add positions.
              </Alert>
            )}
          </Grid>

          {/* Related Supplier Cases */}
          {relatedSupplierCases.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Related Supplier Cases
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {relatedSupplierCases.map((supplierCase) => (
                  <Chip
                    key={supplierCase.id}
                    label={`${supplierCase.case_number} - ${supplierCase.supplier_name}`}
                    variant="outlined"
                    size="small"
                    color={supplierCase.workflow_state === 'converted_to_order' ? 'success' : 'default'}
                  />
                ))}
              </Box>
              {onManageSupplierQuotes && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BusinessIcon />}
                  onClick={onManageSupplierQuotes}
                  sx={{ mt: 1 }}
                >
                  Manage Supplier Quotes
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CustomerPositionOverview;
